import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Globe,
  Copy,
  RefreshCcw,
  Upload,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Users,
  Link as LinkIcon,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { fmtRelative, shortId } from "@/lib/utils";
import type { Account, Proxy, ProxyKind } from "@/lib/types";

export function Proxies() {
  const qc = useQueryClient();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignProxy, setAssignProxy] = useState<Proxy | null>(null);

  const { data: proxies = [], refetch, isFetching } = useQuery({
    queryKey: ["proxies"],
    queryFn: api.listProxies,
  });
  const { data: pool } = useQuery({
    queryKey: ["proxy-pool"],
    queryFn: api.proxyPoolStatus,
    refetchInterval: 10_000,
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: api.listAccounts,
  });

  const stats = useMemo(() => {
    const total = proxies.length;
    const healthy = proxies.filter((p) => p.is_healthy).length;
    const used = proxies.filter((p) => p.in_use_by_account_ids.length > 0).length;
    const free = pool?.free_healthy ?? Math.max(0, healthy - used);
    return { total, healthy, used, free };
  }, [proxies, pool]);

  const del = useMutation({
    mutationFn: api.deleteProxy,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["proxies"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["proxy-pool"] });
    },
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });
  const reset = useMutation({
    mutationFn: api.resetProxy,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["proxies"] });
      qc.invalidateQueries({ queryKey: ["proxy-pool"] });
      if (r.is_healthy) {
        push({ title: "Проверен, снова в строю", variant: "success" });
      } else {
        push({ title: "Прокси всё ещё не работает", description: r.last_error ?? undefined, variant: "destructive" });
      }
    },
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });
  const assign = useMutation({
    mutationFn: ({ id, accountIds }: { id: string; accountIds: string[] }) => api.assignProxy(id, accountIds),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["proxies"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["proxy-pool"] });
      push({ title: `Назначен на ${vars.accountIds.length} аккаунт(ов)`, variant: "success" });
      setAssignProxy(null);
    },
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  function copy(t: string) {
    navigator.clipboard.writeText(t);
    push({ title: "ID скопирован", description: t });
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold">Прокси</h1>
          <p className="text-sm text-muted-foreground">
            {stats.total} всего · <span className={stats.healthy === 0 ? "text-destructive" : "text-foreground"}>{stats.healthy} живых</span> · {stats.used} занято · {stats.free} свободно
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Обновить
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
            <Upload className="h-4 w-4" /> Импорт пачкой
          </Button>
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Добавить
          </Button>
        </div>
      </div>

      {stats.total > 0 && stats.free === 0 && (
        <div className="border-b border-amber-500/30 bg-amber-500/10 px-6 py-2 text-xs text-amber-300">
          Свободных здоровых прокси нет. Новые аккаунты не получится добавить, а сломавшиеся уйдут в no_proxy. Импортируйте новые или сбросьте unhealthy.
        </div>
      )}

      <div className="flex-1 overflow-auto p-6">
        {proxies.length === 0 && !isFetching ? (
          <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            Прокси не настроены. Аккаунты не запустятся без прокси — загрузите пачку через <strong>Импорт пачкой</strong>.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {proxies.map((p) => (
              <ProxyCard
                key={p.id}
                p={p}
                accounts={accounts}
                onCopy={copy}
                onDelete={() => {
                  if (confirm(p.in_use_by_account_ids.length > 0
                    ? `Прокси сейчас используется (${p.in_use_by_account_ids.length}) — после удаления эти аккаунты уйдут в no_proxy. Точно удалить?`
                    : "Удалить прокси?")) del.mutate(p.id);
                }}
                onReset={() => reset.mutate(p.id)}
                onAssign={() => setAssignProxy(p)}
              />
            ))}
          </div>
        )}
      </div>

      <AddProxyDialog open={open} onOpenChange={setOpen} onDone={() => {
        qc.invalidateQueries({ queryKey: ["proxies"] });
        qc.invalidateQueries({ queryKey: ["accounts"] });
        qc.invalidateQueries({ queryKey: ["proxy-pool"] });
      }} />
      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={() => {
        qc.invalidateQueries({ queryKey: ["proxies"] });
        qc.invalidateQueries({ queryKey: ["accounts"] });
        qc.invalidateQueries({ queryKey: ["proxy-pool"] });
      }} />
      <AssignProxyDialog
        proxy={assignProxy}
        accounts={accounts}
        onClose={() => setAssignProxy(null)}
        onSubmit={(accountIds) => {
          if (assignProxy) assign.mutate({ id: assignProxy.id, accountIds });
        }}
        pending={assign.isPending}
      />
    </div>
  );
}

function ProxyCard({
  p, accounts, onCopy, onDelete, onReset, onAssign,
}: {
  p: Proxy;
  accounts: Account[];
  onCopy: (s: string) => void;
  onDelete: () => void;
  onReset: () => void;
  onAssign: () => void;
}) {
  const inUse = p.in_use_by_account_ids.length > 0;
  const holderNames = p.in_use_by_account_ids
    .map((id) => accounts.find((a) => a.id === id))
    .filter((a): a is Account => !!a)
    .map((a) => a.username ? `@${a.username}` : a.phone);
  return (
    <div className="group rounded-lg border border-border bg-card p-4 transition hover:border-primary/40">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-md ${p.is_healthy ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <div className="font-medium">{p.label || `${p.host}:${p.port}`}</div>
            <div className="font-mono text-xs text-muted-foreground">{p.host}:{p.port}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline">{p.kind}</Badge>
          {p.is_healthy ? (
            <Badge variant="success" className="gap-1"><CheckCircle2 className="h-3 w-3" />healthy</Badge>
          ) : (
            <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />unhealthy</Badge>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <LinkIcon className="h-3 w-3" />
          {inUse ? (
            <span title={holderNames.join(", ")} className="font-mono">
              занят: {p.in_use_by_account_ids.length === 1 ? holderNames[0] ?? shortId(p.in_use_by_account_ids[0]) : `${p.in_use_by_account_ids.length} аккаунтов`}
            </span>
          ) : (
            <span>свободен</span>
          )}
        </div>
        {p.failure_count > 0 && (
          <div title={p.last_error || ""}>фейлов: {p.failure_count}</div>
        )}
        {p.last_used_at && (
          <div title={p.last_used_at}>used {fmtRelative(p.last_used_at)}</div>
        )}
        {p.last_failure_at && (
          <div title={p.last_failure_at} className="text-destructive/80">
            fail {fmtRelative(p.last_failure_at)}
          </div>
        )}
      </div>

      {!p.is_healthy && p.last_error && (
        <div className="mt-2 truncate rounded bg-destructive/10 px-2 py-1 font-mono text-[10px] text-destructive/90" title={p.last_error}>
          {p.last_error}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
        <button onClick={() => onCopy(p.id)} className="font-mono hover:text-foreground">
          {shortId(p.id)} <Copy className="inline h-3 w-3 opacity-50" />
        </button>
        <span title={p.created_at}>{fmtRelative(p.created_at)}</span>
      </div>
      <div className="mt-3 flex justify-end gap-1 opacity-0 transition group-hover:opacity-100">
        <Button variant="ghost" size="sm" onClick={onAssign}>
          <Users className="h-4 w-4" /> Назначить
        </Button>
        {!p.is_healthy && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            <RotateCcw className="h-4 w-4" /> Сбросить
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" /> Удалить
        </Button>
      </div>
    </div>
  );
}

function AddProxyDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; onDone: () => void }) {
  const { push } = useToast();
  const [kind, setKind] = useState<ProxyKind>("socks5");
  const [host, setHost] = useState("");
  const [port, setPort] = useState<string>("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState("");
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit() {
    setLoading(true);
    try {
      const created = await api.createProxy({
        kind,
        host: host.trim(),
        port: Number(port),
        secret: secret || null,
        login: login || null,
        password: password || null,
        label: label || null,
      });
      if (created.is_healthy) {
        push({ title: "Прокси создан и проверен", variant: "success" });
      } else {
        push({
          title: "Прокси создан, но не работает",
          description: created.last_error ?? "не удалось подключиться к Telegram",
          variant: "destructive",
        });
      }
      onDone();
      onOpenChange(false);
      setKind("socks5"); setHost(""); setPort(""); setLogin(""); setPassword(""); setSecret(""); setLabel("");
    } catch (e) {
      push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" });
    } finally { setLoading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Добавить прокси</DialogTitle>
          <DialogDescription>Новым аккаунтам прокси назначается автоматически, по одному. Посадить несколько аккаунтов на один прокси можно вручную — кнопка «Назначить» на карточке.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Тип</Label>
              <div className="mt-1.5">
                <Select value={kind} onValueChange={(v) => setKind(v as ProxyKind)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="socks5">SOCKS5</SelectItem>
                    <SelectItem value="mtproto">MTProto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Label</Label>
              <Input className="mt-1.5" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ru-1" />
            </div>
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div>
              <Label>Host</Label>
              <Input className="mt-1.5" value={host} onChange={(e) => setHost(e.target.value)} placeholder="1.2.3.4" />
            </div>
            <div>
              <Label>Port</Label>
              <Input className="mt-1.5" type="number" value={port} onChange={(e) => setPort(e.target.value)} placeholder="1080" />
            </div>
          </div>

          {kind === "socks5" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Login</Label>
                <Input className="mt-1.5" value={login} onChange={(e) => setLogin(e.target.value)} />
              </div>
              <div>
                <Label>Password</Label>
                <Input className="mt-1.5" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </div>
          ) : (
            <div>
              <Label>Secret</Label>
              <Input className="mt-1.5" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="ee…" />
            </div>
          )}

          <Button onClick={submit} disabled={loading || !host || !port} className="w-full">
            {loading ? "Создаю…" : "Создать"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange: (v: boolean) => void; onDone: () => void }) {
  const { push } = useToast();
  const [text, setText] = useState("");
  const [labelPrefix, setLabelPrefix] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ created: number; dup: number; errors: { line: string; error: string }[] } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const txt = await f.text();
    setText((prev) => (prev ? prev + "\n" : "") + txt);
    e.target.value = "";
  }

  async function submit() {
    if (!text.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.bulkImportProxies(text, labelPrefix || null);
      setResult({ created: r.created.length, dup: r.duplicates, errors: r.errors });
      if (r.created.length > 0) {
        const healthy = r.created.filter((p) => p.is_healthy).length;
        const dead = r.created.length - healthy;
        push({
          title: `Импортировано: ${r.created.length}`,
          description: `рабочих: ${healthy}${dead ? ` · мёртвых: ${dead}` : ""} · дублей: ${r.duplicates} · ошибок: ${r.errors.length}`,
          variant: dead ? "default" : "success",
        });
        onDone();
      } else {
        push({
          title: "Ничего не создано",
          description: `дублей: ${r.duplicates} · ошибок: ${r.errors.length}`,
          variant: r.errors.length ? "destructive" : "default",
        });
      }
    } catch (e) {
      push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  function close(v: boolean) {
    onOpenChange(v);
    if (!v) setTimeout(() => { setText(""); setLabelPrefix(""); setResult(null); }, 200);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Импорт пачки прокси</DialogTitle>
          <DialogDescription>
            Одна строка — один прокси. Поддерживаются форматы:
            <code className="ml-1 rounded bg-muted px-1 text-[11px]">host:port</code>,
            <code className="ml-1 rounded bg-muted px-1 text-[11px]">host:port:user:pass</code>,
            <code className="ml-1 rounded bg-muted px-1 text-[11px]">user:pass@host:port</code>,
            <code className="ml-1 rounded bg-muted px-1 text-[11px]">socks5://user:pass@host:port</code>,
            <code className="ml-1 rounded bg-muted px-1 text-[11px]">mtproto://host:port?secret=...</code>.
            Дубликаты (тот же host:port) пропускаются.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label>Список</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept=".txt,text/plain"
                  className="hidden"
                  onChange={onFile}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" /> Из .txt
                </Button>
              </div>
            </div>
            <textarea
              className="mt-1.5 h-48 w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"1.2.3.4:1080:user:pass\n5.6.7.8:1080\nsocks5://u:p@9.9.9.9:1080"}
            />
          </div>
          <div>
            <Label>Префикс label (опционально)</Label>
            <Input
              className="mt-1.5"
              value={labelPrefix}
              onChange={(e) => setLabelPrefix(e.target.value)}
              placeholder="ru"
            />
            <p className="mt-1 text-xs text-muted-foreground">Если задан — у каждой новой прокси будет label вида <code>ru-1</code>, <code>ru-2</code>…</p>
          </div>

          {result && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
              <div>Создано: <strong>{result.created}</strong> · Дублей: <strong>{result.dup}</strong> · Ошибок: <strong>{result.errors.length}</strong></div>
              {result.errors.length > 0 && (
                <div className="mt-2 max-h-40 space-y-1 overflow-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="break-all font-mono text-[11px]">
                      <span className="text-destructive">✗</span> <span className="text-muted-foreground">{e.line}</span> — {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <Button onClick={submit} disabled={loading || !text.trim()} className="w-full">
            {loading ? "Импортирую…" : "Импортировать"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignProxyDialog({
  proxy, accounts, onClose, onSubmit, pending,
}: {
  proxy: Proxy | null;
  accounts: Account[];
  onClose: () => void;
  onSubmit: (accountIds: string[]) => void;
  pending: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected(new Set(proxy?.in_use_by_account_ids ?? []));
  }, [proxy]);

  const allSelected = accounts.length > 0 && selected.size === accounts.length;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(accounts.map((a) => a.id)));
  }

  return (
    <Dialog open={!!proxy} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Назначить прокси на аккаунты</DialogTitle>
          <DialogDescription>
            {proxy?.label || (proxy ? `${proxy.host}:${proxy.port}` : "")} — можно выбрать сразу
            несколько аккаунтов, вплоть до всех. Прокси будет общим для выбранных.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="flex items-center gap-2 border-b border-border pb-2 text-sm font-medium">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            Выбрать все ({accounts.length})
          </label>
          <div className="max-h-72 space-y-1 overflow-auto">
            {accounts.map((a) => {
              const name = a.username ? `@${a.username}` : [a.first_name, a.last_name].filter(Boolean).join(" ") || a.phone;
              return (
                <label key={a.id} className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent/40">
                  <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
                  <span className="flex-1">{name}</span>
                  {a.proxy && a.proxy.id !== proxy?.id && (
                    <span className="font-mono text-xs text-muted-foreground">
                      сейчас: {a.proxy.label || `${a.proxy.host}:${a.proxy.port}`}
                    </span>
                  )}
                </label>
              );
            })}
            {accounts.length === 0 && (
              <div className="py-4 text-center text-xs text-muted-foreground">Нет аккаунтов</div>
            )}
          </div>
          <Button
            className="w-full"
            disabled={selected.size === 0 || pending}
            onClick={() => onSubmit(Array.from(selected))}
          >
            {pending ? "Назначаю…" : `Назначить (${selected.size})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
